/**
 * Typed wrapper around the embedder worker.
 */

import type { FromEmbedWorker, ToEmbedWorker } from "./embedWorker";

export class EmbedClient {
  private worker: Worker;
  private nextId = 1;
  private pending = new Map<number, { resolve: (v: { dim: number; data: Float32Array; ms: number }) => void; reject: (e: Error) => void }>();
  private loadWaiter: {
    resolve: (v: { loadMs: number; dim: number; backend?: string }) => void;
    reject: (e: Error) => void;
    onMessage?: (m: string) => void;
  } | null = null;
  /** Which backend the ladder settled on, once loaded. */
  backend = "";

  constructor() {
    this.worker = new Worker(new URL("./embedWorker.ts", import.meta.url), { type: "module" });
    this.worker.addEventListener("message", (ev: MessageEvent<FromEmbedWorker>) => {
      const m = ev.data;
      switch (m.type) {
        case "loading":
          this.loadWaiter?.onMessage?.(m.message);
          break;
        case "ready":
          this.backend = m.backend ?? this.backend;
          this.loadWaiter?.resolve({ loadMs: m.loadMs, dim: m.dim, backend: m.backend });
          this.loadWaiter = null;
          break;
        case "embedded": {
          const p = this.pending.get(m.id);
          this.pending.delete(m.id);
          p?.resolve({ dim: m.dim, data: m.data, ms: m.ms });
          break;
        }
        case "error": {
          const err = new Error(m.message);
          if (m.id !== undefined) {
            const p = this.pending.get(m.id);
            this.pending.delete(m.id);
            p?.reject(err);
          } else {
            this.loadWaiter?.reject(err);
            this.loadWaiter = null;
          }
          break;
        }
      }
    });
  }

  private send(m: ToEmbedWorker) {
    this.worker.postMessage(m);
  }

  load(hfId: string, device: "webgpu" | "wasm" = "webgpu", onMessage?: (m: string) => void) {
    return new Promise<{ loadMs: number; dim: number; backend?: string }>((resolve, reject) => {
      this.loadWaiter = { resolve, reject, onMessage };
      this.send({ type: "load", hfId, device });
    });
  }

  embed(texts: string[]) {
    const id = this.nextId++;
    return new Promise<{ dim: number; data: Float32Array; ms: number }>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.send({ type: "embed", id, texts });
    });
  }

  /** Embed a single query and return just its vector. */
  async embedOne(text: string): Promise<Float32Array> {
    const { data } = await this.embed([text]);
    return data;
  }

  dispose() {
    this.worker.terminate();
  }
}
